import { createConfig, createStorage, http, type Config, type CreateConnectorFn } from "@wagmi/core";
import { injected } from "@wagmi/connectors/injected";
import { robinhood } from "viem/chains";
import { elysium } from "../config/elysium";
import { hyperEvm } from "../config/bridge";
import type { EIP1193Provider } from "viem";
import { localE2eProvider } from "./local-e2e-provider";

let configuration: Promise<Config> | undefined;

/** Browser-lifetime connector registry; runtime subscribers have a shorter, explicit lifetime. */
export function getWalletConfig(): Promise<Config> {
  return configuration ??= createWalletConfig().catch(error => { configuration = undefined; throw error; });
}

export async function createWalletConfig(): Promise<Config> {
  const testProvider = localE2eProvider();
  const connectors: CreateConnectorFn[] = [injected({ shimDisconnect: true,
    ...(testProvider ? { target: { id: "local-test", name: "Local test wallet",
      provider: () => testProvider as EIP1193Provider } } : {}),
  })];
  const projectId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID?.trim();
  if (projectId && !testProvider) {
    const { walletConnect } = await import("@wagmi/connectors/walletConnect");
    const remote = walletConnect({ projectId, showQrModal: true,
      metadata: { name: import.meta.env.VITE_BRAND_NAME || "MERKLEWOOD", description: "Proof of work · forest rewards",
        url: location.origin, icons: [new URL("/logo.jpg", location.origin).href] },
    });
    connectors.push(config => ({ ...remote(config), setup() {
      // The SDK's setup eagerly creates a relay provider. Connect/reconnect already
      // attach its session listeners; defer initialization until either is requested.
      return Promise.resolve();
    } }));
  }
  return createConfig({ chains: [elysium, robinhood, hyperEvm], connectors,
    transports: { [elysium.id]: http(import.meta.env.VITE_ELYSIUM_RPC_URL?.trim() || undefined), [robinhood.id]: http(),
      [hyperEvm.id]: http(hyperEvm.rpcUrls.default.http[0]) },
    // Only connector preferences are stored here; SIWE remains in its separate session controller.
    storage: createStorage({ key: "merklewood.connectors", storage: {
      getItem: key => { try { return sessionStorage.getItem(key); } catch { return null; } },
      setItem: (key, value) => { try { sessionStorage.setItem(key, value); } catch { /* Optional preference. */ } },
      removeItem: key => { try { sessionStorage.removeItem(key); } catch { /* Optional preference. */ } },
    } }), ssr: false, multiInjectedProviderDiscovery: !testProvider,
  });
}
